import { useState } from "react";
import { cx } from "./ui.tsx";

/**
 * The app's charts, one look everywhere: thin bars in one blue with 4px rounded ends, a quiet scale on clean
 * numbers, a card with the value on hover, and a table for screen readers (one measure per chart, never two scales).
 */

/** Rounds a chart's top up to a clean number (with a clean half for the middle gridline), keeping the bars tall. */
export function niceMax(v: number) {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return ([1, 1.2, 1.5, 2, 3, 4, 5, 6, 8, 10].find((m) => m * p >= v) ?? 10) * p;
}

/** 0, 1.5K, 12K: short labels for the scale. */
export const compact = (v: number) => (v >= 1e6 ? `${+(v / 1e6).toFixed(1)}M` : v >= 1000 ? `${+(v / 1000).toFixed(1)}K` : String(+v.toFixed(2)));

export type Column = { key: string; label: string; value: number; detail?: string };

/** Columns over time (days or weeks). `format` writes a value in full (tooltip, table); `tick` the scale's short form. */
export function ColumnChart({
  data,
  format,
  tick = compact,
  label,
  xLabels,
}: {
  data: Column[];
  format: (v: number) => string;
  tick?: (v: number) => string;
  /** What the chart shows, for screen readers and the table's caption. */
  label: string;
  /** Under the chart: first, middle and last (defaults to those columns' labels). */
  xLabels?: [string, string, string];
}) {
  const [hover, setHover] = useState<number | null>(null);
  const top = niceMax(Math.max(0, ...data.map((d) => d.value)));
  const h = hover !== null ? data[hover] : null;
  const mid = Math.floor((data.length - 1) / 2);
  const under = xLabels ?? [data[0]?.label ?? "", data[mid]?.label ?? "", data[data.length - 1]?.label ?? ""];
  return (
    <div>
      <div className="flex gap-2">
        <div className="relative w-9 shrink-0 text-right text-[11px] text-[var(--muted)] tabular-nums" aria-hidden="true">
          {[1, 0.5, 0].map((f) => (
            <span key={f} className="absolute right-0 -translate-y-1/2" style={{ top: `${(1 - f) * 100}%` }}>{tick(top * f)}</span>
          ))}
        </div>
        <div className="relative h-44 min-w-0 flex-1" onMouseLeave={() => setHover(null)} role="img" aria-label={label}>
          {[1, 0.5].map((f) => (
            <div key={f} className="absolute inset-x-0 h-px bg-[var(--line)]/70" style={{ top: `${(1 - f) * 100}%` }} />
          ))}
          <div className="absolute inset-x-0 bottom-0 h-px bg-[var(--line-strong)]" />
          <div className="absolute inset-0 flex items-end gap-[2px]">
            {data.map((d, i) => (
              <div key={d.key} className="flex h-full flex-1 items-end justify-center" onMouseEnter={() => setHover(i)}>
                <div
                  className={cx("w-full max-w-6 rounded-t-[4px] transition-[background-color,opacity] duration-100", hover === null || hover === i ? "opacity-100" : "opacity-55", hover === i ? "bg-[var(--color-accent)]" : "bg-[var(--chart)]")}
                  style={{ height: d.value ? `${Math.max(1.5, (d.value / top) * 100)}%` : 0 }}
                />
              </div>
            ))}
          </div>
          {h && hover !== null && (
            <div
              className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-xl border border-[var(--line)] bg-[var(--panel)] px-3 py-2 whitespace-nowrap shadow-[var(--shadow-lg)]"
              style={{ left: `clamp(4.5rem, ${((hover + 0.5) / data.length) * 100}%, calc(100% - 4.5rem))`, top: `calc(${(1 - h.value / top) * 100}% - 8px)` }}
            >
              <div className="text-sm font-semibold tabular-nums">{format(h.value)}</div>
              <div className="text-[11px] text-[var(--muted)]">{h.label}{h.detail && ` · ${h.detail}`}</div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 flex justify-between gap-2 pl-11 text-[11px] text-[var(--muted)]">
        {under.map((l, i) => <span key={i}>{l}</span>)}
      </div>
      <table className="sr-only">
        <caption>{label}</caption>
        <tbody>{data.map((d) => <tr key={d.key}><td>{d.label}</td><td>{format(d.value)}</td>{d.detail && <td>{d.detail}</td>}</tr>)}</tbody>
      </table>
    </div>
  );
}

export type BarRow = { key: string; label: string; value: number; note: string; title?: string };

/** A ranked list of bars (models, types of work): the name and its number above a thin bar from the left. */
export function BarList({ rows }: { rows: BarRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <ul className="space-y-3.5">
      {rows.map((r) => (
        <li key={r.key} title={r.title}>
          <div className="mb-1.5 flex justify-between gap-2 text-[12.5px]">
            <span className="truncate">{r.label}</span>
            <span className="shrink-0 text-[var(--muted)] tabular-nums">{r.note}</span>
          </div>
          <div className="h-2 rounded-r-[4px] bg-[var(--chart)]" style={{ width: `${Math.max(1, (r.value / max) * 100)}%` }} />
        </li>
      ))}
    </ul>
  );
}
